import packageInfo from "../../../../package.json";
import { selectCognitoUser } from "../../../state/selectors/auth";
import { getState } from "../../../state/store";
import cloudWatchProvider from "../../aws/cloudWatch/cloudWatchProvider";
import storageService from "../../storage/storageService";
import logLevels from "../logLevels";

const LOG_COUNT_TO_PUSH = 10;
const TIME_TO_PUSH_MS = 5 * 1000;
const PUT_EVENTS_ERROR_ATTEMPTS = 5;

const minLogLevel = process.env.REACT_APP_CLOUDWATCH_LOG_LEVEL ?? logLevels.ERROR.name;
const cloudWatchLogEnabled =
  process.env.REACT_APP_CLOUDWATCH_LOG_SERVICE_ENABLED && process.env.NODE_ENV !== `development`;
const level = logLevels[minLogLevel]?.name ?? logLevels.ERROR.name;

let queue = [];
let isLogExposePossible = true;
let isPushInProgress = false;
let logsExposeTimeout = null;
let currentPutEventsErrorAttempts = 0;

const getLevel = () => logLevels[level]?.id;

const trace = (msg) => log(logLevels.TRACE, msg, undefined);

const debug = (msg) => log(logLevels.DEBUG, msg, undefined);

const info = (msg) => log(logLevels.INFO, msg, undefined);

const error = (msg, error) => log(logLevels.ERROR, msg, error);

const warn = (msg, error) => log(logLevels.WARN, msg, error);

const fatal = (msg, error) => log(logLevels.FATAL, msg, error);

const log = (level, msg, error) => {
  const state = getState();

  const isAuthorized = !!state.auth.cognitoUser;

  const isLoggingAvailable = isAuthorized && cloudWatchLogEnabled && level.id >= getLevel();

  if (isLoggingAvailable) {
    const logMessage = getFullLogMessage(level.name, msg, error);

    addToQueue(logMessage);

    if (isLogExposePossible) {
      if (queue.length >= LOG_COUNT_TO_PUSH - 1 || level >= logLevels.ERROR) {
        exposeEvents();
      } else {
        setTimeOutIfNeeded();
      }
    }
  }
};

const addToQueue = (message) => {
  queue.push({
    message,
    timestamp: Date.now()
  });
};

const getDynamicTimeout = () => {
  if (currentPutEventsErrorAttempts++ >= PUT_EVENTS_ERROR_ATTEMPTS) {
    isLogExposePossible = false;
    return false;
  }
  return currentPutEventsErrorAttempts;
};

const exposeEvents = async () => {
  if (isPushInProgress) {
    setTimeOutIfNeeded();
  } else {
    if (queue.length) {
      isPushInProgress = true;
      const logsToPush = queue.splice(0, queue.length);

      await cloudWatchProvider.putEvents({
        eventMessages: logsToPush,
        onError: () => {
          queue.unshift(...logsToPush); //return logs to queue
          isPushInProgress = false;

          setTimeOutIfNeeded(getDynamicTimeout());
        },
        onComplete: () => {
          currentPutEventsErrorAttempts = 0;
          isPushInProgress = false;
        }
      });
    }
  }
};

const collectData = () => {
  const state = getState();

  const appVersion = getLogValue(packageInfo?.version);
  const cognitoUser = getLogValue(selectCognitoUser()(state)?.username);

  const claims = getLogValue(storageService.getUserClaimsFromStorage());

  return {
    appVersion,
    cognitoUser,
    claims
  };
};

const getLogValue = (prop) => prop || "n/a";

const getFullLogMessage = (level, msg, error) => {
  const collectedData = collectData();
  const date = new Date().toISOString();
  const data = { date, level, message: msg, error, ...collectedData };

  return JSON.stringify(data);
};

const setTimeOutIfNeeded = (timeoutMultiplier) => {
  const timeout = timeoutMultiplier ? timeoutMultiplier * TIME_TO_PUSH_MS : TIME_TO_PUSH_MS;

  if (queue.length && !logsExposeTimeout && isLogExposePossible) {
    logsExposeTimeout = setTimeout(() => {
      logsExposeTimeout = null;
      exposeEvents();
    }, timeout);
  }
};

const cloudWatchLogger = {
  trace,
  debug,
  info,
  warn,
  error,
  fatal
};

export default cloudWatchLogger;
